import {
  useState,
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
} from 'react';
import {
  CollapsibleButtonSt,
  CollapsibleContainerSt,
  CollapsibleContentSt,
  CollapsibleHeadingSt,
  CollapsibleButtonImageSt,
  CollapsibleWrapperSt,
  CollapsibleSelectedOptionsSt,
} from './Collapsible.css';
import { shorten } from './utils';
import { usePlainTranslate } from 'app/shared/components/Translation/hooks/usePlainTranslate/usePlainTranslate';
import { ChevronDown } from '@heycar-uikit/icons';

interface CollapsibleProps {
  header?: ReactNode | string;
  children?: ReactNode;
  openOnLoad?: boolean;
  onToggleOpen?(isOpen?: boolean): void;
  qaID?: string;
  id?: string;
  isLarge?: boolean;
  isDebug?: boolean;
  collapsedValue?: string | string[] | ReactNode;
  className?: string;
  qaContentID?: string;
  noTopBorder?: boolean;
  noBottomBorder?: boolean;
}

const ENTER_KEY = 13;

let openingTimeout = null;
let closingTimeout = null;

const Collapsible = ({
  header,
  children,
  openOnLoad = false,
  onToggleOpen,
  qaID,
  isLarge = false,
  isDebug = false,
  id,
  collapsedValue,
  className = '',
  qaContentID,
  noTopBorder,
  noBottomBorder,
}: CollapsibleProps): ReactElement => {
  const plainTranslate = usePlainTranslate();
  const [isOpen, setIsOpen] = useState(openOnLoad);
  const [isOpening, setIsOpening] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isFocus, setIsFocus] = useState(false);

  /**
   * So we can trigger opening the accordion from the parent component.
   * We don't need to change on `isOpen`, otherwise it will never be able
   * to close (because openOnLoad will remain true).
   */
  useEffect(() => {
    setIsOpen(openOnLoad);
  }, [openOnLoad]); // NOTE: Do not change this, or the collapsible won't be able to close

  // Cleanup transition timeouts
  useEffect(() => {
    return () => {
      clearTimeout(openingTimeout);
      clearTimeout(closingTimeout);
    };
  }, []);

  /**
   * Because transition needs real values for max-height we also have to add this
   * isOpening and isClosing property to make the transition smooth both ways.
   * We want the bottom border to appear after the element is closed, and we want
   * the height to be able to animate using isOpening property.
   */
  const handleToggleOpen = useCallback(() => {
    setIsOpen(!isOpen);
    setIsOpening(true);

    if (onToggleOpen) {
      onToggleOpen(isOpen);
    }

    if (isOpen) {
      setIsClosing(true);
      openingTimeout = setTimeout(() => {
        setIsOpening(false);
      }, 20);
    }

    closingTimeout = setTimeout(() => {
      setIsOpening(false);
      setIsClosing(false);
    }, 300);
  }, [isOpen]);

  const handleFocus = useCallback(() => {
    setIsFocus(true);
  }, []);

  const handleBlur = useCallback(() => {
    setIsFocus(false);
  }, []);

  const handleKeyDown = useCallback(
    event => {
      if (event.keyCode === ENTER_KEY) {
        event.stopPropagation();
        event.preventDefault();
        handleToggleOpen();
      }
    },
    [handleToggleOpen, isOpen],
  );

  const label = plainTranslate({
    id: 'shared.components.filter.mappings.more',
    defaultMessage: 'more',
  });

  return (
    <CollapsibleContainerSt
      className={className}
      id={id}
      isOpen={isOpen}
      isClosing={isClosing}
      noTopBorder={noTopBorder}
      noBottomBorder={noBottomBorder}
    >
      <CollapsibleButtonSt
        aria-expanded={isOpen ? 'true' : 'false'}
        aria-controls={id ? `${id}-content` : undefined}
        className={isFocus ? 'focus' : undefined}
        onClick={handleToggleOpen}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        data-qa={qaID}
        isLarge={isLarge}
      >
        <CollapsibleWrapperSt>
          <CollapsibleHeadingSt>{header}</CollapsibleHeadingSt>
          <CollapsibleButtonImageSt isOpen={isOpen}>
            <ChevronDown />
          </CollapsibleButtonImageSt>
        </CollapsibleWrapperSt>

        {collapsedValue &&
        (!Array.isArray(collapsedValue) || collapsedValue.length > 0) ? (
          <CollapsibleSelectedOptionsSt isOpen={isOpen}>
            {Array.isArray(collapsedValue)
              ? shorten(collapsedValue as string[], label)
              : collapsedValue}
          </CollapsibleSelectedOptionsSt>
        ) : null}
      </CollapsibleButtonSt>
      <CollapsibleContentSt
        id={id ? `${id}-content` : undefined}
        isOpening={isOpening}
        isClosing={isClosing}
        isOpen={isOpen}
        data-qa={qaContentID}
      >
        {!isDebug
          ? children
          : isOpen || isOpening || isClosing
          ? children
          : null}
      </CollapsibleContentSt>
    </CollapsibleContainerSt>
  );
};

export default Collapsible;
